"use client"

import { useEffect, useState } from "react"
import axios from "../api/axios"
import { toast } from "react-toastify"
import { FileText, Plus, Edit2, Trash2, Save, X } from "lucide-react"

function FileType() {
  const [fileType, setfileTypes] = useState([])
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    id: null,
  })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchFileType()
  }, [])

  const fetchFileType = async () => {
    try {
      const res = await axios.get("/api/file-type")
      setfileTypes(res.data)
    } catch (err) {
      console.error("Failed to fetch file type", err)
      toast.error("Failed to fetch file type")
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      if (formData.id) {
        await axios.put(`/api/file-type/${formData.id}`, formData)
        toast.success("File type updated successfully")
      } else {
        await axios.post("/api/file-type", formData)
        toast.success("File type created successfully")
      }
      setFormData({ name: "", description: "", id: null })
      fetchFileType()
    } catch (err) {
      console.error("Failed to save file type", err)
      toast.error("Failed to save file type")
    }
    setLoading(false)
  }

  const handleEdit = (type) => {
    setFormData({ ...type })
  }

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this file type?")) return
    try {
      await axios.delete(`/api/file-type/${id}`)
      toast.success("File type deleted")
      fetchFileType()
    } catch (err) {
      console.error("Failed to delete file type", err)
      toast.error("Delete failed")
    }
  }

  const resetForm = () => {
    setFormData({ name: "", description: "", id: null })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl mb-4">
            <FileText size={32} className="text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">File Type Management</h1>
          <p className="text-gray-600">Create and manage different file types for better organization</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                {formData.id ? (
                  <>
                    <Edit2 size={20} className="mr-2 text-orange-600" />
                    Edit File Type
                  </>
                ) : (
                  <>
                    <Plus size={20} className="mr-2 text-orange-600" />
                    Add New File Type
                  </>
                )}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="shadow-sm focus:ring-orange-500 focus:border-orange-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="shadow-sm focus:ring-orange-500 focus:border-orange-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  {formData.id && (
                    <button
                      type="button"
                      onClick={resetForm}
                      className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    >
                      <X size={16} className="inline-block mr-1" />
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="inline-flex items-center bg-orange-600 border border-transparent rounded-md shadow-sm py-2 px-4 text-sm font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Save size={16} className="mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : formData.id ? (
                      <>
                        <Save size={16} className="mr-2" />
                        Update
                      </>
                    ) : (
                      <>
                        <Save size={16} className="mr-2" />
                        Create
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* List Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Existing File Types ({fileType.length})</h2>
              {fileType.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500">No file types created yet.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {fileType.map((type) => (
                        <tr key={type.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{type.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{type.description}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleEdit(type)}
                              className="text-orange-600 hover:text-orange-900 mr-4"
                            >
                              <Edit2 size={16} className="inline-block" />
                              Edit
                            </button>
                            <button onClick={() => handleDelete(type.id)} className="text-red-600 hover:text-red-900">
                              <Trash2 size={16} className="inline-block" />
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FileType
